import { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AppTable, { ColumnConfig } from "../../../../components/AppTable/AppTable";
import AppSearchSelect from "../../../../components/AppSearchSelect/AppSearchSelect";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { getClaimCancelReasonSelectItems } from "../../../admin/Dictionaries/api/claimCancelReason";
import { SectionPermissionTypeEnum } from "../../../../types/SectionPermissionTypesEnum";
import { SelectItemDto } from "../../../../types/SelectItemDto";
import { Pagination } from "../../../../types/Pagination";
import { formatDate } from "../../../../utils/formatters/DateFormatter";
import { truncateText } from "../../../../utils/textUtils";
import { ClaimHistoricDto } from "../../types";
import './HistoricalClaimsTable.css'
import { ClaimStatusEnum } from "../../types/ClaimStatusEnum";
import TextPill from "../../../../components/TextPill";
import { TimelineStatusEnum } from "../../types/enums";
import { getStatusBudgeColorStyle, getTimelineBudgeColorStyle } from "../../utils/claimStatusStyle";
import ClaimClosedModal from "../modals/ClaimClosedModal";
import { useAlert } from "../../../../hooks/useAlert";
import { cancelClaim } from "../../api/ClaimAPI";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { useQueryClient } from "@tanstack/react-query";
import AlertMessage from "../../../../components/AlertMessage";

interface HistoricalTableProps {
    historyData: Pagination<ClaimHistoricDto>;
    headerItemsArray: Record<string, string>;
    totalPages: number;
    currentPage: number;
    handleExportCsv: () => void;
    pageSize: number;
    onOpenDetail?: (idClaim: number) => void;
}

export default function HistoricalClaimsTable({
    historyData,
    headerItemsArray,
    totalPages,
    currentPage,
    handleExportCsv,
    pageSize
}: HistoricalTableProps) {
    const { t } = useTranslation(["claim", "common"]);

    const navigate = useNavigate();
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
    const [cancelReasonOptions, setCancelReasonOptions] = useState<SelectItemDto[]>([]);
    const [selectedCancelReasons, setSelectedCancelReasons] = useState<SelectItemDto[]>([]);
    const [cancelReasonError, setCancelReasonError] = useState<string | undefined>(undefined);
    const [selectedClaimId, setSelectedClaimId] = useState<number | null>(null);
    const [showEndModal, setShowEndModal] = useState(false);
    const { showAlert, hideAlert, alert, isVisible } = useAlert({ fromLocation: false });
    const queryClient = useQueryClient();


    const handleOpenDelete = async (id?: number) => {
        if (!id) return;
        setPendingDeleteId(id);
        setSelectedCancelReasons([]);
        setCancelReasonError(undefined);
        setShowModal(true);
        try {
            const options = await getClaimCancelReasonSelectItems();
            setCancelReasonOptions(options);
        } catch {
            setCancelReasonOptions([]);
        }
    };


    const onDelete = async (idClaim: number, claimCancelReasonId: number) => {
        try {
            await cancelClaim(idClaim, { claimCancelReasonId: claimCancelReasonId } );
            await queryClient.invalidateQueries({ queryKey: ['getHistoricClaims'] });
            navigate('/claims/historic', {
                state: { message: t("claim:delete.claimDeleteSuccessful"), shouldRefresh: true }
            });
  
        } catch (err) {
            const fetchError = err as ApiErrorResponse;
            const errorMessage =
                fetchError?.message ||
                t("claim:delete.claimDeleteError") || "Error al eliminar la demanda";
            showAlert({ message: errorMessage, type: "danger", duration: 8000 });
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setPendingDeleteId(null);
        setSelectedCancelReasons([]);
        setCancelReasonError(undefined);
        setCancelReasonOptions([]);
    };

    const handleConfirmDelete = async () => {
        if (pendingDeleteId == null) return;
        if (selectedCancelReasons.length === 0) {
            setCancelReasonError(t("common:form.requiredFieldForm"));
            return;
        }
        try {
            setDeletingId(pendingDeleteId);
            await onDelete(pendingDeleteId, selectedCancelReasons[0].id);
        } finally {
            setDeletingId(null);
            handleCloseModal();
        }
    };

    const handleOpenEndModal = (id: number) => {
        setShowEndModal(true);
        setSelectedClaimId(id);
    };

    const handleCloseEndModal = () => {
        setShowEndModal(false);
        setSelectedClaimId(null);
    };

    const columns: ColumnConfig<ClaimHistoricDto>[] = [
        {
            header: headerItemsArray.id,
            key: "id",
            render: (item) => <b>{item.id ?? "-"}</b>,
        },
        {
            header: headerItemsArray.namePacient,
            key: "nameAndSurname",
            render: (item) => (
                <span title={item.nameAndSurname ?? ""}>
                    {item.nameAndSurname ? truncateText(item.nameAndSurname, 20) : "-"}
                </span>
            ),
        },
        {
            header: headerItemsArray.cip,
            key: "cip",
            render: (item) => item.cip ?? "-",
        },
        {
            header: headerItemsArray.eventDate,
            key: "eventDate",
            render: (item) => formatDate(item.eventDate),
        },
        {
            header: headerItemsArray.entryDate,
            key: "entryDate",
            render: (item) => formatDate(item.entryDate),
        },
        {
            header: headerItemsArray.creationDate,
            key: "creationDate",
            render: (item) => formatDate(item.createdDate),
        },
        {
            header: headerItemsArray.timeline,
            key: "timeline",
            render: (item) => (
                (item.timelineDays != undefined && item.timelineDays != null
                    ? <TextPill 
                        label={(`${item.timelineDays?.toString()} d`)}
                        backgroundColor={getTimelineBudgeColorStyle(item.timelineStatus)} 
                        textColor={(item.timelineStatus == TimelineStatusEnum.Out ? "white" : "black")}
                        minWidth="60px" />
                    : <>-</>
                )
            ),
        },
        {
            header: headerItemsArray.alert,
            key: "alert",
            render: (item) =>
                item.alert ? (
                    <TextPill label={t("common:yes")} backgroundColor="#ff7576" />
                ) : (
                    <span>{t("common:no")}</span>
                ),
        },
        {
            header: headerItemsArray.state,
            key: "state",
            render: (item) => {
                const style = getStatusBudgeColorStyle(item.state);
                return (
                    <TextPill
                        label={t(`claim:status.${ClaimStatusEnum[item.state]}`)}
                        backgroundColor={style?.backgroundColor ?? "lightgrey"}
                        textColor={style?.color ?? "#fff"}
                        fontWeight={style?.fontWeight ?? 600}
                    />
                );
            }
        },
        {
            header: headerItemsArray.comment,
            key: "comment",
            render: (item) => (
                <div title={item.comment ?? ""} className="text-start" >
                    {item.comment ? truncateText(item.comment, 90) : "-"}
                </div>
            ),
        },
        {
            header: headerItemsArray.actions,
            key: "actions",
            render: (item) => (
                <div>
                    <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleOpenDelete(item.id)}
                        disabled={deletingId === item.id}
                        aria-disabled={deletingId === item.id}
                        className="btn btn-sm btn-gris2 me-1"
                    >
                        <i className="bi bi-x" title={t("common:buttons.buttonWithdraw")}></i>
                    </Button>
                    {item.state !==  ClaimStatusEnum.Closed &&
                            < Button
                            onClick={() => handleOpenEndModal(item.id)}
                        className="btn btn-sm btn-gris2"
                        style={{ marginRight: 1 }} >
                        <i className="bi bi-flag" title={t("claim:historic.closeButton")}></i>
                    </Button>}
                    <Button
                        onClick={() => navigate(`/edit-claim/${item.id}`)}
                        className="btn btn-sm btn-gris2"
                        style={{ marginRight: 1 }}
                    >
                        <i className="bi bi-pen" title={t("common:buttons.buttonSeeDetail")}></i>
                    </Button>
                </div>
            ),
        },
    ];


    return (
        <div className="container-fluid">
            <ClaimClosedModal
                show={showEndModal}
                claimId={selectedClaimId}
                onClose={handleCloseEndModal}
            />
            <AppTable<ClaimHistoricDto>
                data={historyData.items}
                columns={columns}
                emptyMessage={t("common:pagination.noRecords")}
                hasActions={false}
                sectionPermission={SectionPermissionTypeEnum.CLAIMS}
                t={t}
                hasPagination={true}
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                paginationType="claim"
                onExportCsv={handleExportCsv}
                
            />

            <ModalGlobal
                show={showModal}
                onClose={handleCloseModal}
                title={t("claim:modal.modalDeleteTemplateTitle")}
                body={
                    <>
                    {isVisible && (
                            <AlertMessage
                                message={alert!.message}
                                type={alert!.type}
                                onClose={hideAlert}
                            />
                        )}
                   
                    <Form>
                        <p>{t("claim:modal.modalDeleteTemplateSubtitle")}</p>

                        <Form.Group>
                            <Form.Label>{t("claim:modal.cancelReasonLabel")}</Form.Label>
                            <AppSearchSelect
                                isMulti={true}
                                options={cancelReasonOptions}
                                value={selectedCancelReasons}
                                placeholder={t("common:search")}
                                onChange={(val) => {
                                    setSelectedCancelReasons(Array.isArray(val) ? val : val ? [val] : []);
                                    setCancelReasonError(undefined);
                                }}
                            />
                            {cancelReasonError && (
                                <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
                                    {cancelReasonError}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                        </Form>
                    </>
                }
                onSave={handleConfirmDelete}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                isLoading={deletingId != null}
            />
        </div>
    );
}
